// src/pages/AdminPage.tsx
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, User } from '../context/AuthContext';
import '../App.css';

const API_URL = 'https://keywords-tool-api.maksymafendi.workers.dev';

function AdminPage() {
    const auth = useContext(AuthContext);
    const navigate = useNavigate();

    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const [newUsername, setNewUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [newUserRole, setNewUserRole] = useState<'user' | 'admin'>('user');
    const [adminMessage, setAdminMessage] = useState('');

    // Функция для загрузки/перезагрузки списка пользователей
    const fetchUsers = async () => {
        if (!auth || !auth.token) return;
        setIsLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/admin/users`, {
                headers: { 'Authorization': `Bearer ${auth.token}` }
            });
            if (!response.ok) {
                throw new Error('Не удалось загрузить список пользователей.');
            }
            const users: User[] = await response.json();
            setAllUsers(users);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Загружаем пользователей при первом открытии страницы
    useEffect(() => {
        if (!auth || !auth.token) {
            navigate('/login');
        } else {
            fetchUsers();
        }
    }, []); // Пустой массив зависимостей, чтобы выполнилось один раз

    if (!auth || !auth.user) {
        return null; 
    }

    const showAdminSnackbar = (msg: string) => {
        setAdminMessage(msg);
        setTimeout(() => setAdminMessage(''), 3000);
    };

    // Обработчик для формы добавления пользователя
    const handleRegisterNewUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newUsername.trim() || !newPassword.trim() || !auth.token) return;
        try {
            const response = await fetch(`${API_URL}/api/admin/users/add`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.token}`},
                body: JSON.stringify({ username: newUsername.trim(), password: newPassword, role: newUserRole })
            });
            const data = await response.json();
            if (!response.ok) { throw new Error(data.error || 'Ошибка.'); }

            showAdminSnackbar(data.message || 'Пользователь успешно создан.');
            setNewUsername('');
            setNewPassword('');
            fetchUsers(); // Перезагружаем список пользователей после добавления
        } catch (err: any) {
            showAdminSnackbar(err.message);
        }
    };

    // Обработчик для кнопок блокировки/разблокировки
    const handleToggleBlock = async (username: string, currentStatus: boolean) => {
        if (!auth.token) return;
        const action = currentStatus ? 'разблокировать' : 'заблокировать';
        if (window.confirm(`Вы уверены, что хотите ${action} пользователя ${username}?`)) {
            try {
                const response = await fetch(`${API_URL}/api/admin/users/update-status`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.token}` },
                    body: JSON.stringify({ username, isBlocked: !currentStatus })
                });
                if (!response.ok) { throw new Error('Ошибка при обновлении статуса.'); }
                fetchUsers(); // Перезагружаем список пользователей
                showAdminSnackbar(`Пользователь ${username} ${currentStatus ? 'разблокирован' : 'заблокирован'}.`);
            } catch (err: any) {
                showAdminSnackbar(err.message);
            }
        }
    };

    // Обработчик для кнопки удаления
    const handleDeleteUser = async (username: string) => {
        if (!auth.token) return;
        if (window.confirm(`Вы уверены, что хотите НАВСЕГДА удалить пользователя ${username}?`)) {
            try {
                const response = await fetch(`${API_URL}/api/admin/users/delete`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${auth.token}` },
                    body: JSON.stringify({ username })
                });
                if (!response.ok) { throw new Error('Ошибка при удалении.'); }
                fetchUsers(); // Перезагружаем список пользователей
                showAdminSnackbar(`Пользователь ${username} удален.`);
            } catch (err: any) {
                showAdminSnackbar(err.message);
            }
        }
    };

    return (
        <div className="container">
            <div className="dashboard-header-controls">
                <button className="btn-small" onClick={auth.logout}>Выйти ({auth.user.username})</button>
                <button className="btn-small" onClick={() => navigate('/dashboard')}>Dashboard</button>
            </div>
            <div className="admin-page">
                <h2>Панель администратора</h2>

                {adminMessage && <div id="snackbar" className="show">{adminMessage}</div>}

                <div className="admin-section">
                    <h3>Добавить нового пользователя</h3>
                    <form onSubmit={handleRegisterNewUser} className="add-user-form">
                        <div className="input-group"><label htmlFor="newUsername">Имя пользователя:</label><input type="text" id="newUsername" value={newUsername} onChange={(e) => setNewUsername(e.target.value)} required /></div>
                        <div className="input-group"><label htmlFor="newPassword">Пароль:</label><input type="password" id="newPassword" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required /></div>
                        <div className="input-group"><label htmlFor="newUserRole">Роль:</label><select id="newUserRole" value={newUserRole} onChange={(e) => setNewUserRole(e.target.value as 'user' | 'admin')}><option value="user">Пользователь</option><option value="admin">Администратор</option></select></div>
                        <button type="submit" className="btn">Зарегистрировать пользователя</button>
                    </form>
                </div>

                <div className="admin-section">
                    <h3>Управление пользователями</h3>
                    {isLoading ? ( <p>Загрузка...</p> ) : error ? ( <p style={{ color: 'red' }}>{error}</p> ) : (
                     <table className="users-table">
                            <thead>
                                <tr>
                                    <th>Имя пользователя</th><th>Роль</th><th>Статус</th><th>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allUsers.map((u) => (
                                    <tr key={u.id} className={u.is_blocked ? 'blocked-user' : ''}>
                                        <td>{u.username}</td>
                                        <td>{u.role}</td>
                                        <td>{u.is_blocked ? 'Заблокирован' : 'Активен'}</td>
                                        <td className="user-actions">
                                            {auth.user?.username !== u.username && (
                                                <>
                                                    <button onClick={() => handleToggleBlock(u.username, u.is_blocked)} className={`btn-small ${u.is_blocked ? 'btn-unblock' : 'btn-block'}`}>
                                                        {u.is_blocked ? 'Разблок.' : 'Блок.'}
                                                    </button>
                                                    <button onClick={() => handleDeleteUser(u.username)} className="btn-small btn-delete">
                                                        Удалить
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AdminPage;